import type { tSmartLeadbotConfig } from 'src/pages/Settings/components/SmartLeadbotConfig/SmartLeadbotConfig.types';

// Machine-readable gate outcome. Kept i18n-free so the gate logic is a pure,
// unit-testable function; the hook maps each code to its translated message.
export type tSlbGateCode = 'stageNotBotEnabled' | 'configNotActive' | 'funnelMismatch';

export type tSlbGateResult = {
  code: tSlbGateCode;
  actionLink: string;
};

export type tSlbGateInput = {
  config: tSmartLeadbotConfig | null;
  leadFunnelId?: string;
  leadStageId?: string;
  // Whether the bot is currently ON for this chat. When ON, all gates except the
  // human-stage gate are skipped so the user can always turn the bot OFF.
  enabled: boolean;
};

/**
 * Pure evaluation of the client-side Lead Agent toggle gates, in priority order
 * (stage → config → funnel). Returns the blocking reason, or null when the toggle
 * is allowed. Channel (Gate 4) is validated by the backend only.
 *
 * RET-1562: multi-funnel aware (AGI-16). `funnelIds` is authoritative for which
 * funnels the bot is active on; `funnelId` is the legacy singular fallback. The
 * stage gate resolves the LEAD's own funnel snapshot (stage ids are unique only
 * within a funnel — RET-966), falling back to the singular snapshot for legacy
 * single-funnel configs. This mirrors the backend ToggleAutoRespondUseCase gates so
 * both layers agree; a one-sided fix would either lie to the user or keep blocking.
 */
export function evaluateSlbToggleGate(input: tSlbGateInput): tSlbGateResult | null {
  const { config, leadFunnelId, leadStageId, enabled } = input;

  // Config not loaded / fetch failed → allow (optimistic; backend validates).
  if (!config) return null;

  const effectiveFunnelIds = config.funnelIds?.length
    ? config.funnelIds
    : config.funnelId
      ? [config.funnelId]
      : [];

  // Gate 3 (human stage) is checked first because it must block regardless of the
  // current enabled state — when a lead moves to a human-only stage the toggle must
  // disable immediately, giving visual feedback while the backend catches up.
  const funnelSnapshot =
    (leadFunnelId && config.funnelSnapshots?.[leadFunnelId]) || config.funnelSnapshot;
  if (funnelSnapshot?.stages && leadStageId) {
    const stageSnapshot = funnelSnapshot.stages.find((s) => s.stageId === leadStageId);
    if (stageSnapshot && !stageSnapshot.botEnabled) {
      return { code: 'stageNotBotEnabled', actionLink: '/leadbot/smart-config?tab=funnel' };
    }
  }

  // For the remaining gates, if the bot is already ON allow the toggle (turn OFF).
  if (enabled) return null;

  // Gate 1: SLB must be active for the workspace.
  if (!config.aiRespondEnabled) {
    return { code: 'configNotActive', actionLink: '/leadbot/smart-config' };
  }

  // Gate 2: Lead must be in one of the configured (bot-active) funnels.
  if (
    effectiveFunnelIds.length > 0 &&
    leadFunnelId &&
    !effectiveFunnelIds.includes(leadFunnelId)
  ) {
    return { code: 'funnelMismatch', actionLink: '/leadbot/smart-config?tab=funnel' };
  }

  return null;
}
